/**
 * Merchant profile, operational settings, staff, roles and devices.
 *
 * One repository per bounded area would be tidier, but these are all read by a
 * single Settings screen and share the same permission story. It splits when a
 * second consumer appears.
 */

import { api } from "@/api/client";

export interface MerchantProfile {
  id: string;
  name: string;
  trading_name: string;
  display_name: string;
  slug: string;
  industry_type: string;
  registration_number: string;
  vat_number: string;
  vat_mode: "NONE" | "INCLUSIVE" | "EXCLUSIVE";
  vat_rate: string;
  charges_vat: boolean;
  phone: string;
  whatsapp: string;
  email: string | null;
  currency: string;
  timezone: string;
  business_day_start_hour: number;
}

export interface MerchantSettings {
  enabled_payment_methods: string[];
  max_discount_percent_without_approval: string;
  require_approval_for_refunds: boolean;
  require_reason_for_refunds: boolean;
  cash_rounding_increment: string;
  auto_print_receipt: boolean;
  receipt_footer: string;
  negative_stock_policy: "ALLOW" | "WARN" | "BLOCK";
  default_reorder_level: string;
  expiry_warning_days: number;
  low_stock_alerts_enabled: boolean;
  require_barcode: boolean;
  offline_grant_days: number;
  /** The most one refund may give back with no signal. "0.00" turns them off. */
  offline_refund_limit: string;
  allow_offline_shift_open: boolean;
}

export interface Branch {
  id: string;
  name: string;
  code: string;
  suburb: string;
  city: string;
  stock_grain: "BRANCH" | "SECTION";
  is_primary: boolean;
  is_active: boolean;
  section_count: number;
}

export interface Section {
  id: string;
  branch: string;
  branch_name: string;
  name: string;
  code: string;
  section_type: string;
  is_default: boolean;
  holds_stock: boolean;
  is_active: boolean;
}

export interface StaffMember {
  id: string;
  staff_code: string;
  first_name: string;
  last_name: string;
  display_name: string;
  email: string | null;
  phone: string;
  is_active: boolean;
  has_pin: boolean;
  is_pin_locked: boolean;
  must_set_pin: boolean;
  last_login_at: string | null;
  assignments: {
    role_id: string;
    role_code: string;
    role_name: string;
    scope_type: string;
    scope_id: string | null;
  }[];
}

export interface Role {
  id: string;
  code: string;
  name: string;
  description: string;
  is_system: boolean;
  discount_limit_percent: string;
  assignment_count: number;
}

export interface Device {
  id: string;
  branch: string;
  name: string;
  device_type: string;
  status: "PENDING" | "ACTIVE" | "SUSPENDED" | "RETIRED";
  default_till: string | null;
  till_name: string | null;
  is_paired: boolean;
  last_seen_at: string | null;
  app_version: string;
  pending_operation_count: number;
  version: number;
}

export interface Till {
  id: string;
  branch: string;
  section: string;
  section_name: string;
  name: string;
  code: string;
  receipt_prefix: string;
  is_active: boolean;
}

interface Page<T> {
  count: number;
  results: T[];
}

/** Endpoints paginate; the Settings screen wants the whole (small) list. */
async function all<T>(path: string): Promise<T[]> {
  const body = await api.get<Page<T> | T[]>(path);
  return Array.isArray(body) ? body : body.results;
}

export const settingsRepository = {
  merchant: () => api.get<MerchantProfile>("/merchants/me/"),
  updateMerchant: (patch: Partial<MerchantProfile>) =>
    api.patch<MerchantProfile>("/merchants/me/", patch),

  settings: () => api.get<MerchantSettings>("/merchants/settings/"),
  updateSettings: (patch: Partial<MerchantSettings>) =>
    api.patch<MerchantSettings>("/merchants/settings/", patch),

  branches: () => all<Branch>("/branches/"),
  sections: () => all<Section>("/sections/"),

  staff: () => all<StaffMember>("/users/"),
  roles: () => all<Role>("/roles/"),

  createStaff: (input: {
    staff_code: string;
    first_name: string;
    last_name?: string;
    email?: string | null;
    pin?: string;
    password?: string;
    role_id?: string;
    scope_type?: string;
    scope_id?: string;
  }) => api.post<StaffMember>("/users/", input),

  resetPin: (userId: string, pin: string) =>
    api.post<{ status: string }>(`/users/${userId}/reset-pin/`, { pin }),

  deactivateStaff: (userId: string) => api.delete<void>(`/users/${userId}/`),
  reactivateStaff: (userId: string) =>
    api.post<StaffMember>(`/users/${userId}/reactivate/`),

  tills: () => all<Till>("/tills/"),
  devices: () => all<Device>("/devices/"),

  /**
   * Register a terminal. The pairing code comes back **once** — it is never
   * returned by the list endpoint, so the interface must show it immediately and
   * the manager must write it down or read it out there and then.
   */
  registerDevice: (input: {
    branch: string;
    name: string;
    default_till?: string | null;
  }) =>
    api.post<Device & { pairing_code: string; pairing_expires_at: string }>(
      "/devices/",
      input,
    ),

  reissuePairingCode: (deviceId: string) =>
    api.post<{ pairing_code: string; expires_at: string }>(
      `/devices/${deviceId}/pairing-code/`,
    ),

  suspendDevice: (deviceId: string) =>
    api.post<Device>(`/devices/${deviceId}/suspend/`),

  /**
   * Move a terminal to another till, and so to another section.
   *
   * A terminal has no section of its own — it takes one from its till, which is
   * why this is the reassignment endpoint even though nothing here says
   * "section". `expected_version` is what the caller last read: if a colleague
   * has moved the same terminal in the meantime the request fails with 409
   * rather than quietly overwriting them.
   */
  moveDevice: (
    deviceId: string,
    input: { default_till: string | null; expected_version: number },
  ) => api.patch<Device>(`/devices/${deviceId}/`, input),
};
